import { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import { createAutocompleteFilter } from "pages/utils/autocompleteFilter";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Draggable from "react-draggable";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

function PaperComponent(props) {
  return (
    <Draggable handle=".draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

const formatData = (v) => (v ? String(v).substring(0, 16).replace("T", " ") : "");

export default function Inventarios() {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(false);

  const [centros, setCentros] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [inventarios, setInventarios] = useState([]);

  const [centro, setCentro] = useState(null);
  const [fabricante, setFabricante] = useState(null);
  const [marca, setMarca] = useState(null);
  const [openNovoInventario, setOpenNovoInventario] = useState(false);
  const [incluirEncerrados, setIncluirEncerrados] = useState(false);

  const idutilizador = Number(userData?.id || 0);

  const carregarCentros = async () => {
    const [data, err] = await handleApi(api.post("Stocks/SelInventarioCentrosCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setCentros(
      Array.isArray(data)
        ? data.map((x) => ({
            ...x,
            refcentro: String(x.refcentro || "").trim(),
            centro: String(x.centro || x.refcentro || "").trim(),
          }))
        : []
    );
  };

  const carregarFabricantes = async () => {
    const [data, err] = await handleApi(api.post("Fabricantes/SelFabricantesCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantes(Array.isArray(data) ? data : []);
  };

  const carregarMarcas = async (reffabricante) => {
    setMarcas([]);
    setMarca(null);

    if (!reffabricante) return;

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarioMarcasCombo", { reffabricante, term: "" })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setMarcas(Array.isArray(data) ? data : []);
  };

  const carregarInventarios = async () => {
    if (!idutilizador) return;

    setLoading(true);

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarios", {
        idutilizador,
        incluirEncerrados: incluirEncerrados ? 1 : 0,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setInventarios(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!idutilizador) return;

    setLoading(true);

    Promise.all([carregarCentros(), carregarFabricantes()]).finally(() => setLoading(false));
  }, [idutilizador]);

  useEffect(() => {
    carregarInventarios();
  }, [idutilizador, incluirEncerrados]);

  const criarInventario = async () => {
    if (!centro?.refcentro) {
      showAlert("Seleciona o centro.", "warning");
      return;
    }

    if (!fabricante?.ref) {
      showAlert("Seleciona o fabricante.", "warning");
      return;
    }

    if (!marca?.ref) {
      showAlert("Seleciona a marca.", "warning");
      return;
    }

    setLoading(true);

    const [inventario, err, meta] = await handleApi(
      api.post("Stocks/InsInventario", {
        refcentro: centro.refcentro,
        reffabricante: fabricante.ref,
        refmarca: marca.ref,
        idutilizadorabertura: idutilizador,
        obs: "",
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Inventário criado.", "success");

    setOpenNovoInventario(false);

    setCentro(null);
    setFabricante(null);
    setMarca(null);
    setMarcas([]);

    await carregarInventarios();

    if (inventario?.id) {
      window.open(`/stocks/inventarios/${inventario.id}`, "_blank", "noopener,noreferrer");
    }
  };

  const abrirInventario = (row) => {
    if (!row?.id) return;
    window.open(`/stocks/inventarios/${row.id}`, "_blank", "noopener,noreferrer");
  };

  const columnsInventarios = useMemo(
    () => [
      //   { Header: "ID", accessor: "id", width: 70, minWidth: 60, sortType: "numPt" },
      { Header: "Nome", accessor: "nome", width: 220, minWidth: 160 },
      { Header: "Centro", accessor: "centro", width: 180, minWidth: 140 },
      { Header: "Fabricante", accessor: "fabricante", width: 260, minWidth: 180 },
      { Header: "Marca", accessor: "marca", width: 220, minWidth: 160 },
      {
        Header: "Abertura",
        accessor: "dataabertura",
        width: 150,
        minWidth: 130,
        Cell: ({ value }) => formatData(value),
      },
      {
        Header: "Encerramento",
        accessor: "dataencerramento",
        width: 150,
        minWidth: 130,
        Cell: ({ value }) => formatData(value),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <MDBox mt={2}>
        <ProjectTable
          tableKey="inventarios-lista"
          version="v1"
          title={`Inventários (${inventarios.length})`}
          columns={columnsInventarios}
          data={inventarios}
          loading={loading}
          minWidth={1300}
          maxHeight="48vh"
          emptyMessage="Sem inventários para apresentar."
          defaultSortBy={[{ id: "dataabertura", desc: true }]}
          toolbarLeft={
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirEncerrados}
                    onChange={(e) => setIncluirEncerrados(e.target.checked)}
                    size="small"
                  />
                }
                label={<span style={{ fontSize: 12 }}>Incluir encerrados</span>}
              />
            </>
          }
          toolbarActions={[
            {
              id: "criar-inventario",
              label: "Criar inventário",
              color: "success",
              onClick: () => setOpenNovoInventario(true),
            },
          ]}
          rowActions={[
            {
              id: "abrir",
              icon: <OpenInNewIcon fontSize="small" />,
              tooltip: "Abrir inventário",
              color: "primary",
              onClick: abrirInventario,
            },
          ]}
        />

        <Dialog
          open={openNovoInventario}
          onClose={() => setOpenNovoInventario(false)}
          maxWidth="sm"
          fullWidth
          PaperComponent={PaperComponent}
        >
          <DialogTitle className="draggable-dialog-title" sx={{ cursor: "move" }}>
            Novo inventário
          </DialogTitle>

          <DialogContent dividers>
            <MDBox display="grid" gap={2} pt={0.5}>
              <Autocomplete
                options={centros}
                filterOptions={createAutocompleteFilter((o) =>
                  o?.centro ? `${o.centro} ${o.refcentro}` : o?.refcentro || ""
                )}
                value={centro}
                onChange={(_, v) => setCentro(v)}
                getOptionLabel={(o) =>
                  o?.centro ? `${o.centro} (${o.refcentro})` : o?.refcentro ? o.refcentro : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.refcentro || "") === String(val?.refcentro || "")
                }
                renderInput={(params) => <TextField {...params} label="Centro" size="small" />}
              />

              <Autocomplete
                options={fabricantes}
                filterOptions={createAutocompleteFilter((o) =>
                  o?.nome ? `${o.nome} ${o.ref}` : o?.ref || ""
                )}
                value={fabricante}
                disabled={!centro}
                onChange={(_, v) => {
                  setFabricante(v);
                  carregarMarcas(v?.ref || "");
                }}
                getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.ref || "") === String(val?.ref || "")
                }
                renderInput={(params) => <TextField {...params} label="Fabricante" size="small" />}
              />

              <Autocomplete
                options={marcas}
                filterOptions={createAutocompleteFilter((o) =>
                  o?.nome ? `${o.nome} ${o.ref}` : o?.ref || ""
                )}
                value={marca}
                disabled={!fabricante}
                onChange={(_, v) => setMarca(v)}
                getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.ref || "") === String(val?.ref || "")
                }
                renderInput={(params) => <TextField {...params} label="Marca" size="small" />}
              />
            </MDBox>
          </DialogContent>

          <DialogActions sx={{ flexWrap: "wrap" }}>
            <MDButton color="secondary" onClick={() => setOpenNovoInventario(false)}>
              Cancelar
            </MDButton>

            <MDButton
              color="info"
              onClick={criarInventario}
              disabled={!centro || !fabricante || !marca}
            >
              Criar inventário
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}
